<template>
  <div class="map_holder">
    <BaseLayer
      :map-id="id"
      v-if="showBaseLayerControl"
      :options="baseLayerControlOptions"
    ></BaseLayer>
    <div :id="id" class="map" :style="style"></div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashDebounce from "lodash/debounce";
import { mapGetters } from "vuex";

import { initMap, loadIcons } from "@/common/maps/maps";
import { mapMarkerData } from "@/common/maps/features";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import { handlePingUpdate } from "@/common/maps/animate";
import { getBoundsStringSWNE } from "@/common/maps/utilities";
import { GET_MAPS, GET_REMOVE_MAP, GET_PLACES } from "@/store/actions.type";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_PLACES,
} from "@/store";

const EMITTED_ACTIONS = {
  MAP_ON_LOAD: "map-on-load",
  MAP_MOVE_START: "map-move-start",
  MAP_MOVE_END: "map-move-end",
  MAP_ZOOM_START: "map-zoom-start",
  MAP_ZOOM_END: "map-zoom-end",
  MAP_ON_MOVE_ZOOM_START: "map-on-move-zoom-start",
  MAP_ON_MOVE_ZOOM_END: "map-on-move-zoom-end",
  MAP_ON_IDLE: "map-on-idle",
  MAP_ON_CONTEXT_MENU: "map-on-context-menu",
};

export default {
  name: "Map",
  components: {
    BaseLayer: () => import("@/components/map/BaseLayer"),
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    width: {
      type: String,
    },
    height: {
      type: String,
    },
    options: {
      type: Object,
    },
    showSearchControl: {
      type: Boolean,
      default: false,
    },
    showSearchWithKeywordControl: {
      type: Boolean,
      default: false,
    },
    showBaseLayerControl: {
      type: Boolean,
      default: true,
    },
    baseLayerControlOptions: {
      type: Object,
      default() {
        return {
          position: "absolute",
          btnSize: 36,
          bottom: 48,
          right: 20,
        };
      },
    },
    showPlaces: {
      type: Boolean,
      default: false,
    },
    clearControls: {
      type: Boolean,
      default: false,
    },
    showDistanceControl: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    animatedPing: {},
    timeout: null,
    oldBounds: null,
  }),
  async mounted() {
    if (
      (this.id === MAIN_MAP_ID &&
        this.showPlaces &&
        (!this.places || !this.places.length)) ||
      (this.id !== MAIN_MAP_ID && this.showPlaces)
    ) {
      await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
    }

    let options = this.options || {};

    options.hideScaleControls = false;

    if (!options.container) {
      options.container = this.id;
    }

    if (this.showSearchWithKeywordControl) {
      options.showSearchWithKeyword = this.showSearchWithKeywordControl;
    } else if (this.showSearchControl) {
      options.showSearch = this.showSearchControl;
    }

    if (this.showDistanceControl) {
      options.showDistance = this.showDistanceControl;
    }

    const userBaseLayer = this.getBaseLayerPref();
    if (!options.style && userBaseLayer) {
      options.style = userBaseLayer;
    }
    this.map = initMap(options);

    this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAPS, {
      id: this.id,
      map: this.map,
    });

    this.map.on("load", this.onMapLoad);
    this.map.on("movestart", this.onMoveStart);
    this.map.on("moveend", this.onMoveEnd);
    this.map.on("zoomstart", this.onZoomStart);
    this.map.on("zoomend", this.onZoomEnd);
    this.map.on("styledata", this.onStyleData);
    this.map.on("sourcedata", this.onSourceData);
    this.map.on("idle", this.onIdle);
    this.map.on("styleimagemissing", this.onStyleImageMissing);
    this.map.on("contextmenu", this.onContentMenu);
  },
  updated() {
    if (this.map) {
      this.map.resize();
    }
  },
  computed: {
    ...mapGetters({
      userPreferences: MODULE_PREFIX_USER + "preferences",
      places: MODULE_PREFIX_PLACES + "places",
      ping: MODULE_PREFIX_MAP + "ping",
    }),
    style() {
      let style = {
        outline: "none",
      };

      let width = "100%";
      if (this.width) {
        width = this.width;
      }
      style.width = width;

      if (this.height) {
        style.position = "relative";
        style.height = this.height;
      } else {
        style.position = "absolute";
        style.top = 0;
        style.bottom = 0;
      }
      return style;
    },
    isMainMap() {
      return this.id === MAIN_MAP_ID;
    },
    hasPlaces() {
      return !!(this.places && this.places.length);
    },
  },
  methods: {
    onMapLoad() {
      this.loadIcons();
      if (this.showPlaces) {
        this.addPlacesToMap();
      }
      this.$emit(EMITTED_ACTIONS.MAP_ON_LOAD, this.map);
    },
    onMoveStart() {
      this.$emit(EMITTED_ACTIONS.MAP_MOVE_START, this.map);
      this.mapOnMovedOrZoomedStart();
    },
    onMoveEnd() {
      //don't fire event if bounds haven't actually changed
      let bounds = getBoundsStringSWNE(this.map);
      if (bounds === this.oldBounds) {
        return;
      } else {
        this.oldBounds = bounds;
      }

      this.$emit(EMITTED_ACTIONS.MAP_MOVE_END, this.map);
      this.mapOnMovedOrZoomedEnd();
      this.changeAttribution();
      this.setCommonLayerStyles();
    },
    onZoomStart() {
      this.$emit(EMITTED_ACTIONS.MAP_ZOOM_START, this.map);
      this.mapOnMovedOrZoomedStart();
    },
    onZoomEnd() {
      this.$emit(EMITTED_ACTIONS.MAP_ZOOM_END, this.map);
      this.mapOnMovedOrZoomedEnd();
    },
    onStyleData() {
      this.changeAttribution();
    },
    onSourceData() {
      this.changeAttribution();
    },
    onIdle() {
      this.$emit(EMITTED_ACTIONS.MAP_ON_IDLE, this.map);
    },
    onStyleImageMissing(e) {
      let stylesheetSource = "";
      if (lodashGet(e, "style.stylesheet.sources")) {
        stylesheetSource = e.style.stylesheet.sources;
      }
      console.warn(
        `Map style image missing: "${e.id}"`,
        "Source",
        JSON.stringify(stylesheetSource)
      );
    },
    onContentMenu(e) {
      this.$emit(EMITTED_ACTIONS.MAP_ON_CONTEXT_MENU, e);
    },
    setCommonLayerStyles() {
      if (this.map.getLayer("country-label")) {
        this.map.setPaintProperty("country-label", "text-halo-width", 0.5);
      }
      if (this.map.getLayer("state-label")) {
        this.map.setPaintProperty("state-label", "text-halo-width", 0.5);
      }
      if (this.map.getLayer("settlement-label")) {
        this.map.setPaintProperty("settlement-label", "text-halo-width", 0.5);
      }
      if (this.map.getLayer("settlement-subdivision-label")) {
        this.map.setPaintProperty(
          "settlement-subdivision-label",
          "text-halo-width",
          0.5
        );
      }
      if (this.map.getLayer("airport-label")) {
        this.map.setPaintProperty("airport-label", "text-halo-width", 0.5);
      }
      if (this.map.getLayer("poi-label")) {
        this.map.setPaintProperty("poi-label", "text-halo-width", 0.5);
      }
    },
    changeAttribution() {
      // force the rel="noopener" attribute on the mapbox attributions for performance reasons
      // https://jakearchibald.com/2016/performance-benefits-of-rel-noopener/
      let attributionArr = [].slice.call(
        document.querySelectorAll(".mapboxgl-ctrl-attrib-inner a")
      );
      if (
        attributionArr &&
        Array.isArray(attributionArr) &&
        attributionArr.length
      ) {
        for (let i = 0; i < attributionArr.length; i++) {
          if (attributionArr[i] && !attributionArr[i].getAttribute("rel")) {
            attributionArr[i].setAttribute("rel", "noopener");
          }
        }
      }
    },
    loadIcons() {
      loadIcons(this.map, this.places);
    },
    getBaseLayerPref() {
      if (!this.userPreferences.hasOwnProperty("map")) {
        this.userPreferences.map = {};
      }
      const preferenceName = "baseLayer_" + this.id;
      if (!this.userPreferences.map.hasOwnProperty(preferenceName)) {
        this.userPreferences.map[preferenceName] = this.defaultBaseLayer;
      }
      return this.userPreferences.map[preferenceName];
    },
    handlePingUpdate(pingInfo) {
      handlePingUpdate(this.map, pingInfo);
    },
    mapOnMovedOrZoomedStart: lodashDebounce(function () {
      this.$emit(EMITTED_ACTIONS.MAP_ON_MOVE_ZOOM_START, this.map);
    }, 1000),
    mapOnMovedOrZoomedEnd: lodashDebounce(function () {
      this.$emit(EMITTED_ACTIONS.MAP_ON_MOVE_ZOOM_END, this.map);
    }, 1000),
    addPlacesToMap() {
      const PLACES = lodashGet(this.userPreferences, "places");
      if (this.hasPlaces && PLACES) {
        const MAP = this.map;
        this.timeout = setTimeout(() => {
          PLACES.forEach((place) => {
            if (place.mapped) {
              const PLACE = lodashFind(this.places, [
                "uniqueId",
                place.uniqueId,
              ]);
              if (PLACE) {
                const LAYER_ID = `place-${place.uniqueId}`;
                let iconName = "default";
                if (lodashGet(place, "icons")) {
                  iconName = place.icons;
                }
                const PARAMS = {
                  id: LAYER_ID,
                  noCluster: true,
                  iconName: iconName,
                };
                const OPTIONS = getDefaultMarkerOptions(PARAMS);

                mapMarkerData({
                  id: LAYER_ID,
                  data: PLACE.data,
                  markerOptions: OPTIONS,
                  map: MAP,
                  noCluster: true,
                });
              }
            }
          });
        }, 2000);
      }
    },
  },
  watch: {
    ping: {
      handler() {
        if (this.isMainMap) {
          this.handlePingUpdate(this.ping);
        }
      },
    },
  },
  beforeDestroy() {
    if (this.map) {
      this.map.off("load", this.onMapLoad);
      this.map.off("movestart", this.onMoveStart);
      this.map.off("moveend", this.onMoveEnd);
      this.map.off("zoomstart", this.onZoomStart);
      this.map.off("zoomend", this.onZoomEnd);
      this.map.off("styledata", this.onStyleData);
      this.map.off("sourcedata", this.onSourceData);
      this.map.off("idle", this.onIdle);
      this.map.off("contextmenu", this.onContentMenu);
      this.map.remove();
      delete this.map;
    }

    this.$store.dispatch(MODULE_PREFIX_MAP + GET_REMOVE_MAP, this.id);
    this.animatedPing = null;

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  },
};
</script>

<style>
.map {
  background-color: black;
}
:focus {
  outline: none !important;
}
</style>
